import { useLocation } from "wouter";
import { useUser, UserButton, SignInButton, useAuth } from "@clerk/react";
import { useTranslation } from "react-i18next";
import { safeSetItem } from "@/lib/safeStorage";
import { useQuery } from "@tanstack/react-query";
import { getMe } from "@workspace/api-client-react";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import LanguagePill from "@/components/LanguagePill";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

const SAGE = "#A8B5A2";

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  useEffect(() => {
    function onResize() { setIsDesktop(window.innerWidth >= 1024); }
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktop;
}

export default function SiteNav() {
  const { isSignedIn } = useUser();
  const { isLoaded } = useAuth();
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: isLoaded && !!isSignedIn,
  });

  const userRole = me?.role ?? null;
  const roleConfirmed = !!me?.roleConfirmedAt;
  const isAdmin = !!me?.isAdmin;

  const navBg = "rgba(252,252,252,0.88)";
  const navBorder = "rgba(0,0,0,0.05)";

  function navigateAsDonor() {
    if (isSignedIn) navigate("/donor");
    else { safeSetItem("pendingRole", "donor"); navigate("/sign-up"); }
  }

  function navigateAsDebtor() {
    if (isSignedIn) navigate("/debtor");
    else { safeSetItem("pendingRole", "debtor"); navigate("/sign-up"); }
  }

  const navItems: { path: string; label: string; desc: string }[] = [
    { path: "/", label: t("nav.mission"), desc: t("nav.missionDesc") },
    { path: "/impact", label: t("nav.impact"), desc: t("nav.impactDesc") },
    { path: "/how-it-works", label: t("nav.howItWorks"), desc: t("nav.howItWorksDesc") },
    { path: "/pools", label: t("nav.pools"), desc: t("nav.poolsDesc") },
    { path: "/leaderboard", label: t("nav.leaderboard"), desc: t("nav.leaderboardDesc") },
    { path: "/join", label: t("nav.join"), desc: t("nav.joinDesc") },
    { path: "/about", label: t("nav.about"), desc: "" },
  ];

  const linkBtn = (path: string): React.CSSProperties => ({
    background: location === path ? "hsl(var(--accent))" : "none",
    border: "none",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: location === path ? 400 : 300,
    color: location === path ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
    padding: "6px 10px",
    borderRadius: "8px",
    transition: "color 0.2s ease, background 0.2s ease",
    fontFamily: "inherit",
    letterSpacing: "0.01em",
  });

  function renderNavCtas() {
    if (isAdmin) {
      return (
        <>
          <Button variant="ghost" size="sm" style={{ fontWeight: 300, fontSize: "13px" }} onClick={() => navigate("/admin")}>
            Admin Panel
          </Button>
          <UserButton />
        </>
      );
    }

    if (!isSignedIn) {
      return (
        <>
          <SignInButton mode="modal" forceRedirectUrl="/">
            <Button variant="ghost" size="sm" style={{ fontWeight: 300, fontSize: "13px" }}>{t("nav.signIn")}</Button>
          </SignInButton>
          <Button variant="ghost" size="sm" style={{ fontWeight: 300, fontSize: "13px" }} onClick={navigateAsDonor}>
            {t("nav.donorPortal")}
          </Button>
          <button
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "9px 20px", borderRadius: "50px", fontSize: "13px",
              fontWeight: 300, letterSpacing: "0.04em", cursor: "pointer",
              background: "#0a0a0a", color: "#ffffff", border: "1px solid #0a0a0a",
              transition: "all 0.3s ease",
            }}
            onClick={navigateAsDebtor}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.35)`; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
          >
            {t("nav.getHelp")}
          </button>
        </>
      );
    }

    if (roleConfirmed && userRole === "donor") {
      return (
        <>
          <Button variant="ghost" size="sm" style={{ fontWeight: 300, fontSize: "13px" }} onClick={navigateAsDonor}>
            {t("nav.donorPortal")}
          </Button>
          <UserButton />
        </>
      );
    }

    if (roleConfirmed && userRole === "debtor") {
      return (
        <>
          <Button variant="ghost" size="sm" style={{ fontWeight: 300, fontSize: "13px" }} onClick={navigateAsDebtor}>
            {t("nav.getHelp")}
          </Button>
          <UserButton />
        </>
      );
    }

    return <UserButton />;
  }

  return (
    <nav style={{
      position: "sticky",
      top: 0,
      zIndex: 50,
      overflow: "hidden",
      borderBottom: `1px solid ${navBorder}`,
      background: navBg,
      backdropFilter: "blur(16px)",
      transition: "background 0.3s ease, border-color 0.3s ease",
    }}>
      <div style={{ maxWidth: "1152px", margin: "0 auto", padding: "0 20px", height: "80px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <Logo iconSize={56} />
          </button>
          {isDesktop && (
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {navItems.map(item => (
                <HoverCardPrimitive.Root key={item.path} openDelay={180} closeDelay={100}>
                  <HoverCardPrimitive.Trigger asChild>
                    <button
                      onClick={() => navigate(item.path)}
                      className="nav-link-underline"
                      style={linkBtn(item.path)}
                      onMouseEnter={(e) => {
                        if (location !== item.path) {
                          (e.currentTarget as HTMLButtonElement).style.color = "hsl(var(--foreground))";
                          (e.currentTarget as HTMLButtonElement).style.background = "hsl(var(--accent))";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (location !== item.path) {
                          (e.currentTarget as HTMLButtonElement).style.color = "hsl(var(--muted-foreground))";
                          (e.currentTarget as HTMLButtonElement).style.background = "none";
                        }
                      }}
                    >
                      {item.label}
                    </button>
                  </HoverCardPrimitive.Trigger>
                  {item.desc && (
                    <HoverCardPrimitive.Portal>
                      <HoverCardPrimitive.Content
                        side="bottom"
                        align="center"
                        sideOffset={8}
                        style={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "12px",
                          padding: "14px 16px",
                          width: "200px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          zIndex: 100,
                        }}
                      >
                        <p style={{ fontSize: "12px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.5, margin: 0 }}>
                          {item.desc}
                        </p>
                        <HoverCardPrimitive.Arrow style={{ fill: "hsl(var(--border))" }} />
                      </HoverCardPrimitive.Content>
                    </HoverCardPrimitive.Portal>
                  )}
                </HoverCardPrimitive.Root>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <LanguagePill />
          {isDesktop ? renderNavCtas() : null}
          {!isDesktop && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              style={{
                background: "none",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "8px",
                padding: "6px 8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "hsl(var(--foreground))",
              }}
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          )}
        </div>
      </div>

      {!isDesktop && mobileMenuOpen && (
        <div style={{
          borderTop: "1px solid rgba(0,0,0,0.06)",
          background: "rgba(252,252,252,0.97)",
          padding: "12px 20px 16px",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px", marginBottom: "12px" }}>
            {navItems.map(item => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                style={{
                  background: location === item.path ? `${SAGE}20` : "none",
                  border: "none",
                  textAlign: "left",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: location === item.path ? 400 : 300,
                  cursor: "pointer",
                  borderRadius: "8px",
                  color: location === item.path ? "#3a4a37" : "hsl(var(--foreground))",
                  fontFamily: "inherit",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          <hr style={{ border: "none", borderTop: "1px solid rgba(0,0,0,0.06)", margin: "4px 0 12px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {renderNavCtas()}
          </div>
        </div>
      )}
    </nav>
  );
}
